/**
 * `takeLatest`, but per key rather than outright.
 *
 * WHY THIS EXISTS. `takeLatest` answers "a second request supersedes the
 * first", which is the right concurrency for a control a person clicks twice
 * — and the wrong one the moment a BULK action dispatches the same action for
 * several subjects back to back. Each dispatch then cancels the one before it
 * at its first yield, and only the last subject's work survives. Save All's
 * To Do → Draft promotions hit exactly that: a press over five documents
 * persisted the status of one.
 *
 * `takeEvery` is not the answer either. It keeps every task, so two rapid
 * requests for the SAME subject both run — and where the worker waits for a
 * terminal action (redux-saga's channel is multicast, so every blocked `take`
 * matching an action resolves), one success satisfies both waiters and the
 * work is done twice. That is how one status change came to write two history
 * rows, the older one pairing the first request's note with the second
 * request's status.
 *
 * Keyed, both properties hold: a second request for the same subject cancels
 * the first, and different subjects never interfere.
 *
 * ONE COPY, because the pairs that need it have to AGREE. The mutation and
 * the thing recording it are two watchers over one action, and a recorder
 * whose concurrency does not match the mutation's will either drop rows the
 * mutation wrote or write rows it did not. They were separately hand-rolled
 * and had already drifted apart once.
 */
import type { Task } from 'redux-saga';
import { cancel, fork, take } from 'typed-redux-saga/macro';

export function* takeLatestPerKey<TAction extends { type: string }>(
  pattern: string,
  /**
   * The subject this action is about, or null when it names none.
   *
   * A GENERATOR, because every caller so far has to `select` to resolve it:
   * these actions carry an optional id that defaults to whatever is open,
   * and the key has to apply the same defaulting the worker will — or two
   * requests about one subject get two keys and stop superseding each other.
   */
  keyOf: (action: TAction) => Generator<any, string | null, any>,
  worker: (action: TAction) => Generator<any, unknown, any>
): Generator<any, void, any> {
  const inFlight = new Map<string, Task>();
  while (true) {
    const action: TAction = yield* take(pattern);
    const key = yield* keyOf(action);
    if (!key) {
      // Nothing to supersede and nothing to supersede it. Keyed on a shared
      // placeholder these would all cancel each other, which is the bug
      // this helper exists to prevent, in its purest form.
      yield* fork(worker, action);
      continue;
    }
    // Finished tasks are dropped here rather than when they settle: pruning
    // on completion needs a watcher per task, and this costs one pass over
    // the subjects one visit has touched. Cancelling a finished task is a
    // no-op, so the map is an optimisation either way — but an unbounded one
    // is still a leak, and a long session over a large request is exactly
    // where it would be noticed.
    for (const [settled, task] of inFlight) {
      if (!task.isRunning()) {
        inFlight.delete(settled);
      }
    }
    const previous = inFlight.get(key);
    if (previous) {
      yield* cancel(previous);
    }
    inFlight.set(key, yield* fork(worker, action));
  }
}

export default takeLatestPerKey;
